import { FormTypography, useTranslation } from '@diagentech/exprworks-ui-components';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/App/hooks';
import { theme } from '@/App/theme';
import { DeleteStep, I18nTypes, TermsOfUseStatus } from '@/App/types';
import { GenderIcon } from '@/components/common/GenderIcon';
import {
  DELETE_ACCOUNT_DIALOG_WRAPPER_ID,
  DeleteAccountDialogWrapper,
} from '@/components/dialogs/DeleteAccountDialogWrapper';
import { EDIT_PROFILE_DIALOG_ID, EditAccountDialog } from '@/components/dialogs/EditAccountDialog';
import { TelegramLink } from '@/components/layout/AppBar/TelegramLink';
import { selectPresentationModeEnabled } from '@/features/ui/selectPresentationModeEnabled';
import { selectTermsOfUseStatus } from '@/features/ui/selectTermsOfUseStatus';
import { setError, setShowError, togglePresentationMode } from '@/features/ui/uiSlice';
import { resetLogin } from '@/features/user/login/loginSlice';
import { selectTelegramChannel } from '@/features/user/selectTelegramChannel';
import { selectUserDeleteOpen } from '@/features/user/selectUserDeleteOpen';
import { selectUserEditOpen } from '@/features/user/selectUserEditOpen';
import { selectUserProfile } from '@/features/user/selectUserProfile';
import { resetViolation, setUserDeleteOpen, setUserDeleteStep, setUserEditOpen } from '@/features/user/userSlice';
import { resetViolations } from '@/features/violations/violationsSlice';
import { getAvatar } from '@/helpers/getAvatar';
import { maskNumbers } from '@/helpers/maskNumbers';
import { useAuth } from '@/hooks/useAuth';

interface UserMenuItem {
  id: I18nTypes;
  disabled: boolean;
  divider?: boolean;

  onClick(): void;
}

const USER_MENU_ID = 'user-menu';

export const UserAccountInfo: React.FC = () => {
  const { logout } = useAuth();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const userEditOpen = useAppSelector(selectUserEditOpen);
  const userDeleteOpen = useAppSelector(selectUserDeleteOpen);
  const termsOfUseStatus = useAppSelector(selectTermsOfUseStatus);
  const telegramChannel = useAppSelector(selectTelegramChannel);
  const [anchorElUser, setAnchorElUser] = React.useState<HTMLElement | null>(null);
  const presentationModeEnabled = useAppSelector(selectPresentationModeEnabled);

  const matches = useMediaQuery(theme.breakpoints.up('md'));

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleTogglePresentationMode = useCallback(() => {
    dispatch(togglePresentationMode());
  }, [dispatch]);

  const handleEdit = useCallback(() => {
    dispatch(setUserEditOpen(true));
  }, [dispatch]);

  const handleDelete = useCallback(() => {
    dispatch(setUserDeleteOpen(true));
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    dispatch(resetLogin());
    dispatch(setShowError(false));
    dispatch(setError(null));
    logout();
  }, [dispatch, logout]);

  const handleCancel = useCallback(() => {
    dispatch(setUserEditOpen(false));
    dispatch(setUserDeleteOpen(false));
    dispatch(setUserDeleteStep(DeleteStep.CONFIRM_DELETE));
    dispatch(resetViolation());
    dispatch(resetViolations());
  }, [dispatch]);

  const logoutText = useTranslation('SH.PG.LOGOUT');
  const editProfileText = useTranslation('SH.PG.EDITPROFILE');
  const deleteAccountText = useTranslation('SH.BTN.DELETEACCOUNT');

  const presentationModeButtonId = presentationModeEnabled ? 'SH.BTN.PRESENTATIONMODEOFF' : 'SH.BTN.PRESENTATIONMODEON';
  const presentationModeButtonText = useTranslation(presentationModeButtonId);

  const userMenuItems: UserMenuItem[] = useMemo(
    () => [
      {
        id: presentationModeButtonId,
        disabled: termsOfUseStatus !== TermsOfUseStatus.ACCEPTED,
        onClick: () => handleTogglePresentationMode(),
      },
      {
        id: 'SH.PG.EDITPROFILE',
        disabled: termsOfUseStatus !== TermsOfUseStatus.ACCEPTED,
        onClick: () => handleEdit(),
      },
      {
        id: 'SH.BTN.DELETEACCOUNT',
        disabled: termsOfUseStatus !== TermsOfUseStatus.ACCEPTED,
        divider: true,
        onClick: () => handleDelete(),
      },
      {
        id: 'SH.PG.LOGOUT',
        disabled: false,
        onClick: () => handleLogout(),
      },
    ],
    [presentationModeButtonId, termsOfUseStatus, handleTogglePresentationMode, handleEdit, handleDelete, handleLogout],
  );

  if (!userProfile) {
    return null;
  }

  const { firstName, lastName, dateOfBirth, gender } = userProfile;

  return (
    <>
      {telegramChannel && <TelegramLink url={telegramChannel} />}
      {matches && (
        <>
          <Tooltip title={presentationModeButtonText}>
            <span>
              <IconButton
                size="large"
                aria-label={presentationModeButtonText}
                color="inherit"
                sx={{ p: '4px' }}
                onClick={(_) => handleTogglePresentationMode()}
                disabled={termsOfUseStatus !== TermsOfUseStatus.ACCEPTED}
              >
                {presentationModeEnabled ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={editProfileText}>
            <span>
              <IconButton
                size="large"
                aria-label={editProfileText}
                aria-controls={EDIT_PROFILE_DIALOG_ID}
                aria-haspopup="true"
                color="inherit"
                sx={{ p: '4px' }}
                onClick={(_) => handleEdit()}
                disabled={termsOfUseStatus !== TermsOfUseStatus.ACCEPTED}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={deleteAccountText}>
            <span>
              <IconButton
                size="large"
                aria-label={deleteAccountText}
                aria-controls={DELETE_ACCOUNT_DIALOG_WRAPPER_ID}
                aria-haspopup="true"
                color="inherit"
                sx={{ p: '4px' }}
                onClick={(_) => handleDelete()}
                disabled={termsOfUseStatus !== TermsOfUseStatus.ACCEPTED}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Avatar {...getAvatar(`${firstName} ${lastName}`)} />
          <Box sx={{ marginLeft: '7px' }}>
            <Typography variant="body2" align="justify">
              {firstName} {lastName}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'end' }}>
              <Typography variant="body2" align="justify">
                {presentationModeEnabled ? maskNumbers(dateOfBirth) : dateOfBirth}
              </Typography>
              <GenderIcon gender={gender} />
            </Stack>
          </Box>
          <Tooltip title={logoutText}>
            <span>
              <IconButton
                size="large"
                aria-label={logoutText}
                color="inherit"
                sx={{
                  paddingRight: '5px',
                }}
                onClick={(_) => handleLogout()}
              >
                <LogoutIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      {!matches && (
        <>
          <IconButton aria-controls={USER_MENU_ID} aria-haspopup="true" onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <Avatar {...getAvatar(`${firstName} ${lastName}`)} />
          </IconButton>
          <Menu
            sx={{ mt: '40px' }}
            id={USER_MENU_ID}
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {userMenuItems.map((menuItem) => (
              <MenuItem
                key={menuItem.id}
                disabled={menuItem.disabled}
                divider={menuItem.divider ? menuItem.divider : false}
                onClick={(_) => {
                  handleCloseUserMenu();
                  menuItem.onClick();
                }}
              >
                <FormTypography id={menuItem.id} component="span" textAlign="center" />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      <EditAccountDialog open={userEditOpen} handleCancel={() => handleCancel()} />
      <DeleteAccountDialogWrapper open={userDeleteOpen} handleCancel={() => handleCancel()} />
    </>
  );
};
